import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  countConfirmedForUi,
  countFactsForUi,
  faqIndexFromPath,
  factsForPanel,
  isFactConfirmedForUi,
} from './factsPanelUtils';
import { getBriefPathValue } from './briefPathUtils';

function factPageLabel(fact) {
  if (!fact.sourceUrl) return 'your site';
  try {
    const p = new URL(fact.sourceUrl).pathname.replace(/^\//, '') || 'home';
    return p.replace(/\.html$/, '') || 'home';
  } catch {
    return 'your site';
  }
}

function formatPhoneDisplay(raw) {
  const trimmed = String(raw || '').trim();
  if (!trimmed) return '';
  const digits = trimmed.replace(/\D/g, '');
  if (trimmed.startsWith('+') && digits.length >= 10) {
    const d = digits.length === 11 && digits.startsWith('1') ? digits : `1${digits.slice(-10)}`;
    if (d.length === 11) return `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  }
  if (digits.length === 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  return trimmed;
}

function normalizePhoneE164(displayOrE164) {
  const trimmed = String(displayOrE164 || '').trim();
  const digits = trimmed.replace(/\D/g, '');
  if (trimmed.startsWith('+') && digits.length >= 10) return `+${digits}`;
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return trimmed;
}

function displayFactValue(fact) {
  const v = String(fact.value ?? '');
  if (/\+1\d{10}\b/.test(v.replace(/\s/g, ''))) return formatPhoneDisplay(v);
  if (/phones/i.test(fact.path) && /\+?\d/.test(v)) return formatPhoneDisplay(v);
  return v;
}

/** Prefer the live brief over stale fact rows (e.g. cleared license still in facts for publish checks). */
function factValueForDisplay(fact, brief) {
  if (!brief || typeof brief !== 'object') return displayFactValue(fact);
  const licMatch = /^licenses\[(\d+)\]$/.exec(fact.path);
  if (licMatch) {
    const licenses = brief.licenses;
    if (Array.isArray(licenses)) {
      const lic = licenses[Number(licMatch[1])];
      if (lic == null || String(lic).trim() === '') return '';
      return displayFactValue({ ...fact, value: lic });
    }
  }
  const raw = getBriefPathValue(brief, fact.path);
  if (raw === undefined) return displayFactValue(fact);
  if (raw === null || String(raw).trim() === '') return '';
  return displayFactValue({ ...fact, value: raw });
}

function displayFactEvidence(fact) {
  return String(fact.evidence ?? '').replace(/\+1(\d{10})\b/g, (_, digits) => formatPhoneDisplay(`+${digits}`));
}

function sectionForPath(path) {
  if (path.startsWith('reviews')) return 'Reviews';
  if (path.startsWith('faqs') || path.includes('.faq')) return 'FAQs';
  if (path.startsWith('people')) return 'People';
  if (path.startsWith('services')) return 'Services';
  if (/hours/i.test(path)) return 'Hours';
  if (path.startsWith('contact')) return 'Contact';
  if (path.startsWith('business') || path === 'ownerNotes') return 'Business';
  return 'Business';
}

const SECTION_ORDER = ['Business', 'Contact', 'Hours', 'Services', 'People', 'FAQs', 'Reviews'];

function ConfirmFactButton({ path, onConfirm }) {
  const ref = useRef(null);
  const onConfirmRef = useRef(onConfirm);
  onConfirmRef.current = onConfirm;
  const stableConfirm = useCallback(() => {
    onConfirmRef.current(path);
  }, [path]);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    el.addEventListener('click', stableConfirm);
    return () => el.removeEventListener('click', stableConfirm);
  }, [stableConfirm]);
  return (
    <button
      ref={ref}
      type="button"
      className="fact-confirm-btn"
      style={{
        padding: '0.45rem 0.85rem',
        borderRadius: 6,
        border: 'none',
        background: 'var(--ink, #171A0D)',
        color: '#fff',
        fontWeight: 600,
        cursor: 'pointer',
        position: 'relative',
        zIndex: 2,
      }}
    >
      Confirm
    </button>
  );
}

function FactValueEditor({ fact, brief, isConfirmed, onConfirm, onEditValue, expanded, setExpanded }) {
  const shown = factValueForDisplay(fact, brief);
  const [localValue, setLocalValue] = useState(shown);
  const editingRef = useRef(false);
  const valueRef = useRef(null);
  /** Holds last committed text until parent `fact.value` catches up (avoids stale snapshot wiping the row). */
  const pendingCommitRef = useRef(null);
  const useContentEditable = fact.path === 'contact.serviceArea';

  useEffect(() => {
    if (editingRef.current) return;
    if (pendingCommitRef.current !== null) {
      const pending = pendingCommitRef.current;
      const parentVal = String(fact.value ?? '');
      const caughtUp = /phones/i.test(fact.path)
        ? normalizePhoneE164(parentVal) === normalizePhoneE164(pending)
        : String(shown) === String(pending);
      if (caughtUp) pendingCommitRef.current = null;
      else return;
    }
    setLocalValue(shown);
  }, [shown, fact.path, fact.value]);

  const syncEditorText = (el) => {
    valueRef.current = el;
    if (!useContentEditable || !el || editingRef.current) return;
    const text = String(localValue ?? '');
    if (el.innerText !== text) el.textContent = text;
  };

  useEffect(() => {
    if (useContentEditable) syncEditorText(valueRef.current);
  }, [localValue, shown, fact.path, useContentEditable]);

  const commitValue = (nextRaw) => {
    let next = String(nextRaw ?? '').trim();
    if (/phones/i.test(fact.path)) {
      const e164 = normalizePhoneE164(next);
      pendingCommitRef.current = e164;
      setLocalValue(formatPhoneDisplay(e164));
      onEditValue(fact.path, e164);
      return;
    }
    pendingCommitRef.current = next;
    setLocalValue(next);
    onEditValue(fact.path, next);
  };

  const valueField = useContentEditable ? (
    <div
      ref={syncEditorText}
      className="fact-value"
      contentEditable
      suppressContentEditableWarning
      role="textbox"
      aria-label={`Fact value for ${fact.path}`}
      onFocus={(e) => {
        editingRef.current = true;
        e.currentTarget.scrollIntoView({ block: 'center', behavior: 'instant' });
      }}
      onInput={(e) => setLocalValue(e.currentTarget.innerText)}
      onBlur={(e) => {
        const next = e.currentTarget.innerText.trim();
        e.currentTarget.textContent = next;
        commitValue(next);
        editingRef.current = false;
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.currentTarget.blur();
        }
      }}
      style={{
        flex: '1 1 160px',
        padding: '0.45rem 0.6rem',
        borderRadius: 6,
        border: '1px solid #e5e7eb',
        color: '#111827',
        background: '#fff',
        minHeight: '2.25rem',
        outline: 'none',
        width: '100%',
        whiteSpace: 'pre-wrap',
      }}
    />
  ) : (
    <input
      className="fact-value"
      type="text"
      value={localValue}
      aria-label={`Fact value for ${fact.path}`}
      onFocus={(e) => {
        editingRef.current = true;
        e.currentTarget.scrollIntoView({ block: 'center', behavior: 'instant' });
      }}
      onChange={(e) => setLocalValue(e.target.value)}
      onBlur={(e) => {
        commitValue(e.target.value);
        editingRef.current = false;
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.currentTarget.blur();
        }
      }}
      style={{
        flex: '1 1 160px',
        padding: '0.45rem 0.6rem',
        borderRadius: 6,
        border: '1px solid #e5e7eb',
        color: '#111827',
        background: '#fff',
        minHeight: '2.25rem',
        outline: 'none',
        width: '100%',
      }}
    />
  );

  return (
    <div
      data-fact-path={fact.path}
      className="fact-row"
      style={{
        marginBottom: '1rem',
        padding: '0.75rem',
        borderRadius: 8,
        border: `1px solid ${isConfirmed ? '#e5e7eb' : '#d1d5db'}`,
        background: isConfirmed ? '#f9fafb' : '#fff',
        opacity: isConfirmed ? 0.85 : 1,
      }}
    >
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {valueField}
        {!isConfirmed && <ConfirmFactButton path={fact.path} onConfirm={onConfirm} />}
        {isConfirmed && (
          <span style={{ color: '#6b7280', fontSize: '0.85rem', alignSelf: 'center' }}>✓</span>
        )}
      </div>
      <div style={{ marginTop: '0.35rem', fontSize: '0.8rem' }}>
        <a href={fact.sourceUrl || '#'} onClick={(e) => e.preventDefault()} style={{ color: '#6b7280' }}>
          from /{factPageLabel(fact)}
        </a>
        {' · '}
        <button
          type="button"
          onClick={() => setExpanded((s) => ({ ...s, [fact.path]: !s[fact.path] }))}
          style={{ background: 'none', border: 'none', color: '#4b5563', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
        >
          Show source
        </button>
      </div>
      {expanded[fact.path] && fact.evidence && (
        <p
          className="fact-evidence"
          style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#374151', lineHeight: 1.45 }}
          title={displayFactEvidence(fact)}
        >
          {displayFactEvidence(fact)}
        </p>
      )}
    </div>
  );
}

export default function FactsPanel({ facts, brief, confirmedPaths, onConfirm, onEditValue }) {
  const [expanded, setExpanded] = useState({});
  const panelFacts = useMemo(() => factsForPanel(facts, brief), [facts, brief]);
  const total = countFactsForUi(facts, brief);
  const confirmed = countConfirmedForUi(facts, confirmedPaths, brief);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const fact of panelFacts) {
      const section = sectionForPath(fact.path);
      if (!map.has(section)) map.set(section, []);
      map.get(section).push(fact);
    }
    return SECTION_ORDER.filter((s) => map.has(s)).map((section) => ({ section, items: map.get(section) }));
  }, [panelFacts]);

  const renderFactRows = (items) =>
    items.map((fact) => (
      <FactValueEditor
        key={fact.path}
        fact={fact}
        brief={brief}
        isConfirmed={isFactConfirmedForUi(fact, confirmedPaths)}
        onConfirm={onConfirm}
        onEditValue={onEditValue}
        expanded={expanded}
        setExpanded={setExpanded}
      />
    ));

  return (
    <div className="facts-panel" style={{ padding: '0 0.5rem' }}>
      <p style={{ fontWeight: 700, marginBottom: '1rem', color: 'var(--ink, #171A0D)', fontSize: '0.95rem' }}>
        {confirmed} of {total} facts confirmed
      </p>
      {grouped.map(({ section, items }) => {
        if (section === 'FAQs') {
          const pairCount = new Set(items.map((f) => faqIndexFromPath(f.path)).filter((n) => n !== null)).size;
          return (
            <details
              key={section}
              data-fact-group="FAQs"
              className="fact-group"
              style={{ marginBottom: '1.25rem' }}
            >
              <summary
                style={{
                  fontSize: '0.8rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  color: '#6b7280',
                  margin: '0 0 0.65rem',
                  cursor: 'pointer',
                  listStyle: 'none',
                }}
              >
                FAQs ({pairCount || Math.ceil(items.length / 2)})
              </summary>
              {renderFactRows(items)}
            </details>
          );
        }
        return (
          <section key={section} data-fact-group={section.toLowerCase()} className="fact-group" style={{ marginBottom: '1.25rem' }}>
            <h3 style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6b7280', margin: '0 0 0.65rem' }}>
              {section}
            </h3>
            {renderFactRows(items)}
          </section>
        );
      })}
    </div>
  );
}
